import {
  ChangeDetectorRef, ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import {DOCUMENT} from '@angular/common';

import {ngbAutoClose} from '../util/autoclose';
import {PlacementArray, positionElements} from '../util/positioning';

import {NgbDateAdapter} from './adapters/ngb-date-adapter';
import {DbDatepickerComponent, IDatepickerNavigateEvent} from './datepicker';
import {DbCalendar} from './db-calendar';
import {DbDate} from './db-date';
import {DbDateParserFormatter} from './ngb-date-parser-formatter';
import {DbDateStruct} from './date-struct';
import {DbInputDatepickerConfig} from './datepicker-input-config';
import {isString} from '../util/util';

/**
 * A directive that allows to stick a datepicker popup to an input field.
 *
 * Manages interaction with the input field itself, does value formatting and provides forms integration.
 */
@Directive({
    selector: '[dbDatepicker]',
    exportAs: 'dbDatepicker',
    standalone: false
})
export class DbInputDatepickerDirective implements OnChanges,
    OnDestroy {

  private _cRef: ComponentRef<DbDatepickerComponent>| null = null;
  private _disabled = false;
  private _model: DbDate | null = null;
  private _zoneSubscription: any;

  @Input() autoClose: boolean | 'inside' | 'outside';

  /** The number of months to display.*/
  @Input() displayMonths: number;

  /** The first day of the week.*/
  @Input() firstDayOfWeek: number;

  /** The reference to the custom template for the datepicker footer.*/
  @Input() footerTemplate: TemplateRef<any>;

  /**
   * The callback to mark some dates as disabled.
   *
   * It is called for each new date when navigating to a different month.
   *
   * `current` is the month that is currently displayed by the datepicker.
   */
  @Input() markDisabled: (date: DbDate, current?: {year: number, month: number}) => boolean;

  /**
   * The earliest date that can be displayed or selected. Also used for form validation.
   *
   * If not provided, 'year' select box will display 10 years before the current month.
   */
  @Input() minDate: DbDateStruct;

  /**
   * The latest date that can be displayed or selected. Also used for form validation.
   *
   * If not provided, 'year' select box will display 10 years after the current month.
   */
  @Input() maxDate: DbDateStruct;

  /**
   * The way of displaying days that don't belong to the current month.
   *
   * * `"visible"` - days are visible
   * * `"hidden"` - days are hidden, white space preserved
   * * `"collapsed"` - days are collapsed, so the datepicker height might change between months
   *
   * For the 2+ months view, days in between months are never shown.
   */
  @Input() outsideDays: 'visible' | 'collapsed' | 'hidden';

  /**
   * The preferred placement of the datepicker popup.
   *
   * Possible values are `"top"`, `"top-left"`, `"top-right"`, `"bottom"`, `"bottom-left"`,
   * `"bottom-right"`, `"left"`, `"left-top"`, `"left-bottom"`, `"right"`, `"right-top"`,
   * `"right-bottom"`
   *
   * Accepts an array of strings or a string with space separated possible values.
   *
   * The default order of preference is `"bottom-left bottom-right top-left top-right"`
   *
   * Please see the [positioning overview](#/positioning) for more details.
   */
  @Input() placement: PlacementArray;

  /**
   * The date to open calendar with.
   *
   * With the default calendar we use ISO 8601: 'month' is 1=Jan ... 12=Dec.
   * If nothing or invalid date is provided, calendar will open with current month.
   *
   * You could use `navigateTo(date)` method as an alternative.
   */
  @Input() startDate: DbDateStruct;

  /** The last selected date to open calendar with.*/
  @Input() endDate: DbDateStruct | null;

  @Input() canSelectRange: boolean;

  /** Emit dateSelect() on every date selection.*/
  @Input() selectOnClick: boolean;

  /**
   * A css selector or html element specifying the element the datepicker popup should be positioned against.
   *
   * By default the input is used as a target.*/
  @Input() positionTarget: string | HTMLElement;

  /**
   * An event emitted when user selects a date using keyboard or mouse.
   *
   * The payload of the event is currently selected `DbDate`.*/
  @Output() dateSelect = new EventEmitter<[DbDate, DbDate]>();

  /**
   * An event emitted when user press Clear button.
   *
   * The payload of the event is void.
   */
  @Output() dateClear = new EventEmitter<void>();

  /**
   * Event emitted right after the navigation happens and displayed month changes.
   *
   * See [`IDatepickerNavigateEvent`](#/components/datepicker/api#IDatepickerNavigateEvent) for the payload info.
   */
  @Output() navigate = new EventEmitter<IDatepickerNavigateEvent>();

  /** An event fired after closing datepicker window.*/
  @Output() closed = new EventEmitter<void>();

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    this._disabled = value === '' || (value && value !== 'false');
  }

  constructor(
      private _parserFormatter: DbDateParserFormatter, private _elRef: ElementRef<HTMLInputElement>,
      private _vcRef: ViewContainerRef, private _renderer: Renderer2, private _ngZone: NgZone,
      private _calendar: DbCalendar, private _dateAdapter: NgbDateAdapter<any>,
      private _cfResolver: ComponentFactoryResolver,
      @Inject(DOCUMENT) private _document: any, private _changeDetector: ChangeDetectorRef,
      config: DbInputDatepickerConfig) {
    ['autoClose', 'positionTarget', 'placement'].forEach(input => this[input] = config[input]);
    this._zoneSubscription = _ngZone.onStable.subscribe(() => this._updatePopupPosition());
  }

  isOpen() { return !!this._cRef; }

  open() {
    if (!this.isOpen()) {
      const cf = this._cfResolver.resolveComponentFactory(DbDatepickerComponent);
      this._cRef = this._vcRef.createComponent(cf);

      this._applyPopupStyling(this._cRef.location.nativeElement);
      this._applyDatepickerInputs(this._cRef.instance);
      this._subscribeForDatepickerOutputs(this._cRef.instance);
      this._cRef.instance.ngOnInit();

      this._cRef.changeDetectorRef.detectChanges();

      this._document.querySelector('body').appendChild(this._cRef.location.nativeElement);

      ngbAutoClose(
          this._ngZone, this._document, this.autoClose, () => this.close(), this.closed, [],
          [this._elRef.nativeElement, this._cRef.location.nativeElement]);
    }
  }

  /**
   * Closes the datepicker popup.
   */
  close() {
    if (this.isOpen()) {
      this._vcRef.remove(this._vcRef.indexOf(this._cRef !.hostView));
      this._cRef = null;
      this.closed.emit();
      this._changeDetector.markForCheck();
    }
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['minDate'] || changes['maxDate']) {

      if (this.isOpen()) {
        if (changes['minDate']) {
          this._cRef !.instance.minDate = this.minDate;
        }
        if (changes['maxDate']) {
          this._cRef !.instance.maxDate = this.maxDate;
        }
        this._cRef !.instance.ngOnChanges(changes);
      }
    }

    if (changes['datepickerClass']) {
      const {currentValue, previousValue} = changes['datepickerClass'];
      this._applyPopupClass(currentValue, previousValue);
    }
  }

  ngOnDestroy() {
    this.close();
    this._zoneSubscription.unsubscribe();
  }

  private _applyDatepickerInputs(datepickerInstance: DbDatepickerComponent): void {
    ['displayMonths', 'firstDayOfWeek', 'footerTemplate', 'markDisabled', 'minDate', 'maxDate', 'navigation', 'outsideDays', 'canSelectRange', 'selectOnClick']
        .forEach((optionName: string) => {
          if (this[optionName] !== undefined) {
            datepickerInstance[optionName] = this[optionName];
          }
        });
    datepickerInstance.startDate = this.startDate || this._model;
    datepickerInstance.endDate = this.endDate || null;
  }

  private _applyPopupClass(newClass: string, oldClass?: string) {
    const popupEl = this._cRef && this._cRef.location.nativeElement;
    if (popupEl) {
      if (newClass) {
        this._renderer.addClass(popupEl, newClass);
      }
      if (oldClass) {
        this._renderer.removeClass(popupEl, oldClass);
      }
    }
  }

  private _applyPopupStyling(nativeElement: any) {
    this._renderer.addClass(nativeElement, 'db-datepicker-body');
  }

  private _subscribeForDatepickerOutputs(datepickerInstance: DbDatepickerComponent) {
    datepickerInstance.navigate.subscribe(navigateEvent => this.navigate.emit(navigateEvent));
    datepickerInstance.dateSelect.subscribe(date => {
      this.dateSelect.emit(date);
      if (this.autoClose === true || this.autoClose === 'inside' || !this.selectOnClick) {
        this.close();
      }
    });
    datepickerInstance.dateClear.subscribe(() => {
      this.dateClear.emit();
      this.close();
    });
  }

  private _updatePopupPosition() {
    if (!this._cRef) {
      return;
    }

    let hostElement: HTMLElement;
    if (isString(this.positionTarget)) {
      hostElement = this._document.querySelector(this.positionTarget);
    } else if (this.positionTarget instanceof HTMLElement) {
      hostElement = this.positionTarget;
    } else {
      hostElement = this._elRef.nativeElement;
    }

    if (this.positionTarget && !hostElement) {
      throw new Error('ngbDatepicker could not find element declared in [positionTarget] to position against.');
    }

    positionElements(hostElement, this._cRef.location.nativeElement, this.placement, true);
  }
}
